import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { authService, AuthUser, usersService } from '@/lib/api';
import { UserData } from '@/types/user';
import posthog from 'posthog-js';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';

interface AuthContextProps {
  user: AuthUser | null;
  userData: UserData | null;
  loading: boolean;
  signUp: (email: string, password: string, invitationToken?: string) => Promise<AuthUser>;
  signIn: (email: string, password: string) => Promise<AuthUser>;
  signInWithGoogle: (idToken: string, invitationToken?: string, context?: 'login' | 'signup') => Promise<AuthUser>;
  logout: () => Promise<void>;
  logoutAllSessions: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (token: string, newPassword: string) => Promise<void>;
  updatePasswordWithSupabaseToken: (accessToken: string, newPassword: string, refreshToken?: string) => Promise<void>;
  refreshUserData: () => Promise<void>;
  deleteUserAccount: () => Promise<void>;
  reinitializeAuth: () => Promise<void>;
  getUserSessions: () => Promise<any[]>;
  getLoginAttempts: (limit?: number) => Promise<any[]>;
  getLockoutHistory: (limit?: number) => Promise<any[]>;
  sessionExpiryWarning: number | null;
}

export const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpiryWarning, setSessionExpiryWarning] = useState<number | null>(null);

  const convertAuthUserToUserData = (authUser: AuthUser): UserData => {
    return {
      uid: authUser.id,
      email: authUser.email,
      displayName: authUser.displayName,
      photoURL: authUser.photoURL,
      emailVerified: authUser.email_verified,
      createdAt: new Date(authUser.createdAt),
      updatedAt: new Date(authUser.updatedAt),
      // Add any additional fields from the user profile
      timezone: undefined, // Will be populated from user profile if available
    };
  };

  const fetchUserData = async (authUser: AuthUser) => {
    try {
      // Get the latest user profile data
      const profileResponse = await usersService.getCurrentUserProfile();
      
      if (profileResponse.success && profileResponse.data) {
        const userData = convertAuthUserToUserData(profileResponse.data);
        // Add additional profile fields
        userData.timezone = profileResponse.data.timezone;
        setUserData(userData);
      } else {
        // Fallback to auth user data
        setUserData(convertAuthUserToUserData(authUser));
      }
    } catch (error) {
      console.error("Error fetching user profile data:", error);
      // Fallback to auth user data
      setUserData(convertAuthUserToUserData(authUser));
    }
  };

  const setupUserTracking = (userData: UserData) => {
    try {
      // Identify the user in PostHog
      posthog.identify(userData.uid, {
        name: userData.displayName,
        email: userData.email,
        createdAt: userData.createdAt,
        updatedAt: userData.updatedAt,
        photoURL: userData.photoURL,
      });
    } catch (error) {
      console.error("Error identifying user in PostHog:", error);
    }
    
    // Set Sentry user context
    setSentryUser({
      id: userData.uid,
      email: userData.email || undefined,
      username: userData.displayName || undefined,
    });
  };

  const clearUserTracking = () => {
    try {
      posthog.reset();
    } catch (error) {
      console.error("Error resetting PostHog:", error);
    }
    clearSentryUser();
  };

  // Initialize auth state on app start
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Set up session expiry callbacks
        authService.setSessionExpiryCallback(() => {
          console.log('Session expired via timeout, logging out...');
          setUser(null);
          setUserData(null);
          setSessionExpiryWarning(null);
          clearUserTracking();
          // Note: SessionManager component will handle UI notifications and redirects
        });

        authService.setSessionExpiryWarningCallback((minutesLeft: number) => {
          console.log(`Session expires in ${minutesLeft} minutes`);
          setSessionExpiryWarning(minutesLeft);
        });

        const authUser = await authService.initializeAuth();
        
        if (authUser) {
          setUser(authUser);
          await fetchUserData(authUser);
        } else {
          setUser(null);
          setUserData(null);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        setUser(null);
        setUserData(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Update tracking when userData changes
  useEffect(() => {
    if (userData) {
      setupUserTracking(userData);
    } else {
      clearUserTracking();
    }
  }, [userData]);

  const signUp = async (email: string, password: string, invitationToken?: string): Promise<AuthUser> => {
    try {
      const response = await authService.signUp({
        email,
        password,
        invitation_token: invitationToken,
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Sign up failed');
      }

      const authUser = response.data.user;
      setUser(authUser);
      await fetchUserData(authUser);

      return authUser;
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  };

  const signIn = async (email: string, password: string): Promise<AuthUser> => {
    try {
      const response = await authService.signIn({ email, password });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Sign in failed');
      }

      const authUser = response.data.user;
      setUser(authUser);
      await fetchUserData(authUser);

      return authUser;
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signInWithGoogle = async (
    idToken: string, 
    invitationToken?: string, 
    context?: 'login' | 'signup'
  ): Promise<AuthUser> => {
    try {
      const response = await authService.signInWithGoogle({
        id_token: idToken,
        invitation_token: invitationToken,
        context: context,
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Google sign in failed');
      }

      const authUser = response.data.user;
      setUser(authUser);
      await fetchUserData(authUser);

      return authUser;
    } catch (error) {
      console.error('Google sign in error:', error);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.signOut();
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local logout even if API call fails
    } finally {
      setUser(null);
      setUserData(null);
      setSessionExpiryWarning(null);
      clearUserTracking();
    }
  };

  const logoutAllSessions = async (): Promise<void> => {
    try {
      await authService.logoutAllSessions();
    } catch (error) {
      console.error('Logout all sessions error:', error);
      // Continue with local logout even if API call fails
    } finally {
      setUser(null);
      setUserData(null);
      setSessionExpiryWarning(null);
      clearUserTracking();
    }
  };

  const getUserSessions = async (): Promise<any[]> => {
    try {
      const response = await authService.getUserSessions();
      if (response.success && response.data) {
        return response.data.sessions;
      }
      return [];
    } catch (error) {
      console.error('Get user sessions error:', error);
      return [];
    }
  };

  const getLoginAttempts = async (limit: number = 50): Promise<any[]> => {
    try {
      const response = await authService.getLoginAttempts(limit);
      if (response.success && response.data) {
        return response.data.login_attempts;
      }
      return [];
    } catch (error) {
      console.error('Get login attempts error:', error);
      return [];
    }
  };

  const getLockoutHistory = async (limit: number = 10): Promise<any[]> => {
    try {
      const response = await authService.getLockoutHistory(limit);
      if (response.success && response.data) {
        return response.data.lockout_history;
      }
      return [];
    } catch (error) {
      console.error('Get lockout history error:', error);
      return [];
    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    try {
      const response = await authService.requestPasswordReset({ email });

      if (!response.success) {
        throw new Error(response.error || 'Password reset failed');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  const confirmPasswordReset = async (token: string, newPassword: string): Promise<void> => {
    try {
      const response = await authService.confirmPasswordReset({
        token,
        new_password: newPassword,
      });

      if (!response.success) {
        throw new Error(response.error || 'Password reset confirmation failed');
      }
    } catch (error) {
      console.error('Password reset confirmation error:', error);
      throw error;
    }
  };

  const updatePasswordWithSupabaseToken = async (accessToken: string, newPassword: string, refreshToken?: string): Promise<void> => {
    try {
      const response = await authService.updatePasswordWithSupabaseToken({
        access_token: accessToken,
        new_password: newPassword,
        refresh_token: refreshToken,
      });

      if (!response.success) {
        throw new Error(response.error || 'Password update failed');
      }
    } catch (error) {
      console.error('Supabase password update error:', error);
      throw error;
    }
  };

  const refreshUserData = async (): Promise<void> => {
    if (!user) return;

    try {
      const response = await authService.refreshUserData();

      if (response.success && response.data) {
        setUser(response.data);
        await fetchUserData(response.data);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const deleteUserAccount = async (): Promise<void> => {
    try {
      const response = await authService.deleteAccount();

      if (!response.success) {
        throw new Error(response.error || 'Account deletion failed');
      }

      // Clear local state
      setUser(null);
      setUserData(null);
      clearUserTracking();
    } catch (error) {
      console.error('Account deletion error:', error);
      throw error;
    }
  };

  const reinitializeAuth = async (): Promise<void> => {
    setLoading(true);
    try {
      const authUser = await authService.initializeAuth();
      
      if (authUser) {
        setUser(authUser);
        await fetchUserData(authUser);
      } else {
        setUser(null);
        setUserData(null);
      }
    } catch (error) {
      console.error("Error reinitializing auth:", error);
      setUser(null);
      setUserData(null);
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextProps = {
    user,
    userData,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    logout,
    logoutAllSessions,
    resetPassword,
    confirmPasswordReset,
    updatePasswordWithSupabaseToken,
    refreshUserData,
    deleteUserAccount,
    reinitializeAuth,
    getUserSessions,
    getLoginAttempts,
    getLockoutHistory,
    sessionExpiryWarning,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default useAuth;
